import type { Branch } from "@dentacore/shared-types";
import { useEffect, useRef, useState } from "react";

import { listBranches } from "../api/branches";
import { activeBranch } from "../api/tokens";
import { useActiveBranch } from "../api/use-active-branch";
import type { CalendarLocations } from "../calendar/calendar-selection";
import { useCalendarLocations } from "../calendar/calendar-selection";
import { LocationBadge } from "../calendar/location-badge";
import picker from "./branch-picker.module.css";
import styles from "./shell.module.css";

/**
 * Picks the active branch, from whatever branches actually exist.
 *
 * **Nothing here assumes a count.** A clinic may have one branch or twenty, and
 * both must work: with one, the control is a plain label rather than a
 * pointless `<select>` of a single option; with several it is a real picker.
 * The number, the names and the order all come from the database.
 *
 * Choosing a branch reloads the page. That is deliberate rather than lazy —
 * every branch-scoped query in the application is cached against the old
 * branch, and a reload is the one way to be certain nothing from the previous
 * branch survives on screen. It can become a cache invalidation once there is
 * a cache to invalidate.
 *
 * ── What it *shows* is the global, not a local copy ────────────────────────
 *
 * **This used to hold the current branch in its own `useState`**, seeded once
 * on mount, and it went stale the moment anything else wrote the global. Two
 * things do: opening a location's settings page has always called
 * `activeBranch.set` as a side effect of navigating, and the Settings rail's
 * switcher now does it deliberately. Either left this control displaying the
 * branch you used to be on while the rest of the screen showed the one you are
 * on — caught in the browser while building the rail.
 *
 * So the displayed value comes from `useActiveBranch`, which subscribes. The
 * reload on *change* stays exactly as it was: other screens still depend on it,
 * and removing it is a separate piece of work with its own risk.
 */
export function BranchSwitcher(): React.JSX.Element | null {
  const [branches, setBranches] = useState<Branch[] | null>(null);
  const current = useActiveBranch();
  const calendar = useCalendarLocations();

  useEffect(() => {
    let cancelled = false;

    listBranches()
      .then((all) => {
        if (cancelled) return;

        // **Only the branches this user is rostered to.** Switching to a branch
        // is "work in it", and `BranchScopeGuard` admits on `branch_memberships`
        // rather than on role — so an admin, who is listed every branch in the
        // clinic, was being offered ones whose every subsequent request 403s.
        // The old comment here claimed the switcher "can never offer a branch
        // that then 403s"; for admins that was simply untrue, and the client hit
        // it. Administering a branch you do not work in is still possible — that
        // is the Settings screen, which lists them all and marks these.
        const loaded = all.filter((branch) => branch.isMember);
        setBranches(loaded);

        // Nothing chosen yet, or a stale choice for a branch this user has
        // since lost access to. Fall back to the first one they do have.
        const chosen = activeBranch.get();
        const valid = chosen !== null && loaded.some((branch) => branch.id === chosen);
        if (!valid) {
          // `setCurrent` is gone — writing the global is what updates the
          // displayed value now, because the display subscribes to it.
          activeBranch.set(loaded[0]?.id ?? null);
        }
      })
      .catch(() => {
        if (!cancelled) setBranches([]);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (branches === null || branches.length === 0) return null;

  // **Several locations in one calendar** (CF-D1): for whoever may combine them
  // and has two or more to combine. Everyone else keeps the plain switcher.
  if (calendar.selection?.canCombine === true && calendar.reachable.length >= 2) {
    return <LocationPicker calendar={calendar} current={current} />;
  }

  if (branches.length === 1) {
    return (
      <div className={styles.branchLabel} data-testid="branch-switcher">
        {branches[0]?.name}
      </div>
    );
  }

  return (
    <select
      className={styles.branchSelect}
      data-testid="branch-switcher"
      aria-label="Active branch"
      value={current ?? ""}
      onChange={(event) => {
        activeBranch.set(event.target.value);
        window.location.reload();
      }}
    >
      {/*
        **The active branch may not be one of these, and the label must not
        lie.** This control lists only branches the caller is rostered to,
        because switching to one means working in it. Settings deliberately
        lists them all — an admin may edit a location's hours without being
        rostered there (decision S7) — so its rail can make a branch active
        that has no option here, and a `<select>` whose value matches nothing
        renders as its first option: this control would then name a branch
        nobody had chosen.

        Disabled, because it is not somewhere you can switch *to*; present,
        because it is where you are.
      */}
      {current !== null && !branches.some((branch) => branch.id === current) && (
        <option value={current} disabled>
          Administering another location
        </option>
      )}
      {branches.map((branch) => (
        <option key={branch.id} value={branch.id}>
          {branch.name}
        </option>
      ))}
    </select>
  );
}

/**
 * **The locations shown in one calendar** (CF-D1, CF-D12): *All my locations*,
 * then each location with its badge and today's hours. One ticked is today's
 * single calendar, and makes that location the active one; several are drawn
 * together. The choice is saved on the account the moment it changes, and the
 * menu stays open so several can be ticked in a row.
 */
function LocationPicker({
  calendar,
  current,
}: {
  calendar: CalendarLocations;
  current: string | null;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const box = useRef<HTMLDivElement | null>(null);
  const button = useRef<HTMLButtonElement | null>(null);
  const { shown, reachable } = calendar;
  const all = reachable.map((location) => location.id);

  // Closes on a click elsewhere and on Escape, focus back on the button.
  useEffect(() => {
    if (!open) return;
    const onDown = (event: PointerEvent): void => {
      if (box.current !== null && !box.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      setOpen(false);
      button.current?.focus();
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const nameOf = (id: string): string =>
    reachable.find((location) => location.id === id)?.name ?? "This location";
  const label =
    shown.length === all.length
      ? `All my locations (${String(all.length)})`
      : shown.length === 1
        ? nameOf(shown[0] ?? current ?? "")
        : `${String(shown.length)} locations`;

  const choose = (ids: readonly string[]): void => {
    setSaving(true);
    setError(null);
    calendar
      .choose(ids)
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : "That choice did not save."),
      )
      .finally(() => setSaving(false));
  };

  return (
    <div className={picker.wrap} ref={box}>
      <button
        ref={button}
        type="button"
        className={picker.button}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
        data-testid="branch-switcher"
      >
        <span className={picker.label}>{label}</span>
        <span aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className={picker.menu} role="group" aria-label="Locations shown in the calendar">
          <p className={picker.note}>Show these locations in one calendar</p>
          <label className={picker.row}>
            <input
              type="checkbox"
              checked={shown.length === all.length}
              disabled={saving}
              onChange={(event) => choose(event.target.checked ? all : [current ?? all[0] ?? ""])}
              data-testid="picker-all"
            />
            <b>All my locations</b>
          </label>
          <hr className={picker.rule} />
          {reachable.map((location) => {
            const ticked = shown.includes(location.id);
            return (
              <label key={location.id} className={picker.row}>
                <input
                  type="checkbox"
                  checked={ticked}
                  disabled={saving}
                  onChange={(event) => {
                    const next = event.target.checked
                      ? [...shown, location.id]
                      : shown.filter((id) => id !== location.id);
                    // Nothing ticked is not a calendar: unticking the last keeps it.
                    choose(next.length === 0 ? [location.id] : next);
                  }}
                  data-testid="picker-location"
                  data-branch-id={location.id}
                />
                <LocationBadge code={location.code} hue={location.hue} name={location.name} />
                <span className={picker.name}>{location.name}</span>
                <span className={picker.meta}>
                  {location.opensAt === null || location.closesAt === null
                    ? "Closed today"
                    : `${location.opensAt}–${location.closesAt}`}
                </span>
              </label>
            );
          })}
          <p className={picker.note}>
            One ticked is that location&rsquo;s own calendar. Remembered for next time.
          </p>
          {error !== null && (
            <p className={picker.error} role="alert">
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
